import { apiClient } from '@api/client';
import type { GlobalToken, ThemeConfig } from 'antd';

export interface MapPreferences {
  selectedLayers?: string[];
  defaultMapId?: number;
  defaultTileId?: number;
}

export interface WidgetConfig {
  id: string;
  enabled: boolean;
  position: number;
}

export interface ThemeOverrides {
  tokens?: Partial<ThemeConfig['token']>;
  components?: Partial<ThemeConfig['components']>;
}

export interface WidgetSettings {
  widgets?: WidgetConfig[];
  theme_overrides?: ThemeOverrides;
}

export interface DevicePreferences {
  pinned_interfaces?: Record<string, number[]>; // "deviceId" -> []ifIndex
  version?: number; // Версия для синхронизации закрепленных портов
}

export interface MenuItemOrder {
  key: string;
  order: number;
  hidden?: boolean;
}

export interface UserSettings {
  theme: string;
  language: string;
  region_id: number;
  map_preferences?: MapPreferences;
  widget_settings?: WidgetSettings;
  device_preferences?: DevicePreferences;
  menu_order?: MenuItemOrder[];
}

export interface UpdateUserSettingsRequest {
  theme?: string;
  language?: string;
  region_id?: number;
  map_preferences?: MapPreferences;
  widget_settings?: WidgetSettings;
  device_preferences?: DevicePreferences;
  menu_order?: MenuItemOrder[];
}

export const userSettingsApi = {
  /**
   * Получить настройки текущего пользователя
   * GET /api/v1/user/settings
   */
  getSettings: async (): Promise<UserSettings> => {
    const response = await apiClient.get<UserSettings>('/user/settings');
    return response.data;
  },

  /**
   * Обновить настройки текущего пользователя
   * PUT /api/v1/user/settings
   */
  updateSettings: async (data: UpdateUserSettingsRequest): Promise<UserSettings> => {
    const response = await apiClient.put<UserSettings>('/user/settings', data);
    return response.data;
  },

  /**
   * Обновить только предпочтения карты
   * PUT /api/v1/user/settings (только map_preferences)
   */
  updateMapPreferences: async (preferences: MapPreferences): Promise<UserSettings> => {
    return userSettingsApi.updateSettings({ map_preferences: preferences });
  },

  /**
   * Обновить только настройки виджетов Dashboard
   * PUT /api/v1/user/settings (только widget_settings)
   */
  updateWidgetSettings: async (widgetSettings: WidgetSettings): Promise<UserSettings> => {
    return userSettingsApi.updateSettings({ widget_settings: widgetSettings });
  },

  /**
   * Обновить только порядок меню
   * PUT /api/v1/user/settings (только menu_order)
   */
  updateMenuOrder: async (menuOrder: MenuItemOrder[]): Promise<UserSettings> => {
    return userSettingsApi.updateSettings({ menu_order: menuOrder });
  },
};

